import api from '../../../api/axios';
import { API_ENDPOINTS } from '../../../api/endpoints';

export interface EmployeeNotification {
  id: number;
  kind: string;
  title: string;
  body: string | null;
  /** A web route to open on click, or null. */
  linkPath: string | null;
  refType: string | null;
  refId: number | null;
  /** `yyyy-MM-dd HH:mm:ss` */
  createdDate: string;
  read: boolean;
}

export interface NotificationInbox {
  unreadCount: number;
  items: EmployeeNotification[];
}

/** `common.dto.ApiResponse`: PascalCase, and `success` fills `Data1`. */
interface ApiEnvelope<T> {
  Data1?: T;
  Message?: string;
}

/**
 * Which bell: `vessel` = vessel notices only (their own bell, so they are never lost among mail and
 * job notices); `other` = everything else. Without a group the server returns all (the app does that).
 */
export type NoticeGroup = 'vessel' | 'other';

/**
 * The vessel notice kinds, the same set the server's `?group=vessel` filters by: an ETA change (or a
 * job ETA that differs from PTP or Westports), a job to link to its voyage, a linked Westports call
 * omitted (link Vessel changes), PTP's or MMSW's schedule not updating, the Westports Excel due for upload
 * (link the Westports schedule page), and phone pushes not working (sent to Super Admins, no link).
 */
export const VESSEL_NOTICE_KINDS = [
  'VESSEL_ETA_CHANGED',
  'VESSEL_LINK_NEEDED',
  'VESSEL_OMITTED',
  'PTP_SCHEDULE_STALE',
  'WESTPORTS_UPLOAD_DUE',
  'MMSW_SCHEDULE_STALE',
  'PHONE_PUSH_DOWN',
] as const;
export type VesselNoticeKind = (typeof VESSEL_NOTICE_KINDS)[number];

export const isVesselNotice = (kind: string): kind is VesselNoticeKind =>
  (VESSEL_NOTICE_KINDS as readonly string[]).includes(kind);

const VESSEL_NOTICE_HEADING: Record<VesselNoticeKind, string> = {
  VESSEL_ETA_CHANGED: 'Vessel ETA change',
  VESSEL_LINK_NEEDED: 'Vessel link needed',
  VESSEL_OMITTED: 'Vessel omitted',
  PTP_SCHEDULE_STALE: 'PTP schedule',
  WESTPORTS_UPLOAD_DUE: 'Westports schedule',
  MMSW_SCHEDULE_STALE: 'MMSW schedule',
  PHONE_PUSH_DOWN: 'Phone alerts',
};

/** The short red heading of a vessel notice (toast and desktop pop-up). */
export const vesselNoticeHeading = (kind: string): string =>
  VESSEL_NOTICE_HEADING[isVesselNotice(kind) ? kind : 'VESSEL_ETA_CHANGED'];

const groupParams = (group?: NoticeGroup) => (group ? { params: { group } } : undefined);

export const fetchMyNotifications = async (group?: NoticeGroup): Promise<NotificationInbox> => {
  const { data } = await api.get<ApiEnvelope<NotificationInbox>>(API_ENDPOINTS.EMPLOYEE_NOTIFICATIONS.MINE, groupParams(group));
  return data?.Data1 ?? { unreadCount: 0, items: [] };
};

export const markNotificationRead = async (id: number): Promise<void> => {
  await api.put(API_ENDPOINTS.EMPLOYEE_NOTIFICATIONS.READ(id));
};

/** Marks read only the notices of that bell. */
export const markAllNotificationsRead = async (group?: NoticeGroup): Promise<void> => {
  await api.put(API_ENDPOINTS.EMPLOYEE_NOTIFICATIONS.READ_ALL, undefined, groupParams(group));
};
